import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { isArray, isEmpty, isString } from 'lodash';
import { captureException } from '@sentry/react';
import { Link } from 'components/Link';
import { OrderedList, UnorderedList } from 'components/Lists';
import { Heading, Paragraph } from 'components/Typography';
import { Document } from '@contentful/rich-text-types';

type Props = {
  /** Expected to only contain one unordered-list */
  children: Document;
};

export const RichText: React.FunctionComponent<Props> = ({ children: richTextDocument }) => {
  return (
    <>
      {documentToReactComponents(richTextDocument, {
        renderNode: {
          [BLOCKS.UL_LIST]: (_, children) => {
            return (
              <UnorderedList useDefaultMargins className="gap-8">
                {children}
              </UnorderedList>
            );
          },
          [BLOCKS.OL_LIST]: (_, children) => {
            return (
              <OrderedList useDefaultMargins className="gap-8">
                {children}
              </OrderedList>
            );
          },
          [BLOCKS.LIST_ITEM]: (_, children) => {
            return <UnorderedList.Item>{children}</UnorderedList.Item>;
          },
          [BLOCKS.PARAGRAPH]: (_, children) => {
            // @NOTE(juan): Workaround for https://github.com/contentful/rich-text/issues/101
            if (isArray(children) && children.every(isEmpty)) {
              return '';
            }
            return <Paragraph useDefaultMargins>{children}</Paragraph>;
          },
          [INLINES.HYPERLINK]: ({ data }, children) => {
            if (!isString(data.uri)) {
              /** @NOTE(juan): I dont think this should ever happen but the rich text library
               *  doesnt have great types so I dont know for sure
               * WIll keep around for a while to see if we get any reports
               */
              captureException(`Bad or no URI for HYPERLINK rich text node, data: ${data}`);
              return children;
            }
            return (
              <Link to={data.uri} includeDefaultClasses disableExternalLinkIcon>
                {children}
              </Link>
            );
          },
          [BLOCKS.HEADING_1]: (_, children) => {
            return (
              <Heading useDefaultMargins level="h1">
                {children}
              </Heading>
            );
          },
          [BLOCKS.HEADING_2]: (_, children) => {
            return (
              <Heading useDefaultMargins level="h2">
                {children}
              </Heading>
            );
          },
          [BLOCKS.HEADING_3]: (_, children) => {
            return (
              <Heading useDefaultMargins level="h3">
                {children}
              </Heading>
            );
          },
          [BLOCKS.HEADING_4]: (_, children) => {
            return (
              <Heading useDefaultMargins level="h4">
                {children}
              </Heading>
            );
          },
          [BLOCKS.HEADING_5]: (_, children) => {
            return (
              <Heading level="h5" useDefaultMargins>
                {children}
              </Heading>
            );
          },
          [BLOCKS.HEADING_6]: (_, children) => {
            return (
              <Heading useDefaultMargins level="h6">
                {children}
              </Heading>
            );
          },
        },

        renderMark: {
          [MARKS.BOLD]: text => <b className="font-bold">{text}</b>,
          [MARKS.ITALIC]: text => <i className="italic">{text}</i>,
        },

        renderText: text => {
          return text;
        },
      })}
    </>
  );
};
